// App.tsx
// BOT-EDIT: 2025-01-27T10:45:00Z Add SubscriptionProvider and invoice pages to App.tsx
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AddToHomeScreen from './components/AddToHomeScreen';
import PWAInstallBanner from './components/PWAInstallBanner';
import { pwaService } from './services/pwaService';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CardCreatePage from './pages/CardCreatePage';
import FirebaseTestPage from './pages/FirebaseTestPage';
import UserProfilePage from './pages/UserProfilePage';
import QRDesignPage from './pages/QRDesignPage';
import CardEditorPage from './pages/CardEditorPage';
import InvoiceCreationPage from './pages/InvoiceCreationPage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import InvoiceListPage from './pages/InvoiceListPage';
import InvoiceSuccessPage from './pages/InvoiceSuccessPage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import StatsDetailPage from './pages/StatsDetailPage';
import DomainSettingsPage from './pages/DomainSettingsPage';
import CustomDomainSettingsPage from './pages/CustomDomainSettingsPage';
import StripePaywallPage from './pages/StripePaywallPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import SubscriptionManagementPage from './pages/SubscriptionManagementPage';
import PWATestPage from './pages/PWATestPage';
import ContactPage from './pages/ContactPage';
import FeaturesPage from './pages/FeaturesPage';
import HelpCenterPage from './pages/HelpCenterPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';

function App() {
  console.log('[App.tsx] App component rendering...');
  console.log('[App.tsx] Using LandingPage as main page');
  
  const { i18n } = useTranslation();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  
  useEffect(() => {
    console.log('🌐 App - Current language:', i18n.language);
    console.log('🌐 App - Available languages:', i18n.languages);
    console.log('🌐 App - Is initialized:', i18n.isInitialized);
    
    // 언어 변경 시 리렌더링 강제
    const handleLanguageChange = () => {
      console.log('🌐 App - Language changed to:', i18n.language);
      // 상태 업데이트로 리렌더링 유도
      window.dispatchEvent(new Event('languageChanged'));
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // PWA 초기화
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const initializePWA = async () => {
      console.log('🚀 ScanVCF PWA 초기화 시작');
      
      // 서비스 워커 등록
      await pwaService.registerServiceWorker();
      
      // Install prompt 상태 감지
      unsubscribe = pwaService.onInstallPromptChange((prompt) => {
        const { showModal } = pwaService.shouldShowAnyPrompt();
        console.log('🔍 ScanVCF PWA 프롬프트 표시 여부:', showModal, 'prompt available:', !!prompt);
        setShowInstallPrompt(showModal);
      });
    };

    initializePWA();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // PWA 설치 프롬프트 핸들러
  const handleInstallPromptClose = () => {
    setShowInstallPrompt(false);
    pwaService.markPromptShown();
  };

  const handleInstallPromptInstall = () => {
    setShowInstallPrompt(false);
    pwaService.markPromptShown();
  };

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <div className="App">
          <Routes>
            {/* 랜딩 페이지 - 헤더/푸터 없음 */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Features 페이지 - 랜딩페이지용 헤더/푸터 사용 */}
            <Route path="/features" element={<FeaturesPage />} />
            
            {/* 일반 페이지들 - 헤더/푸터 있음 */}
            <Route path="/home" element={
              <Layout>
                <HomePage />
              </Layout>
            } />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={
              <Layout showFooter={false}>
                <RegisterPage />
              </Layout>
            } />
            <Route path="/forgot-password" element={
              <Layout showFooter={false}>
                <ForgotPasswordPage />
              </Layout>
            } />
            <Route path="/reset-password" element={
              <Layout showFooter={false}>
                <ResetPasswordPage />
              </Layout>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/card/create" element={
              <ProtectedRoute>
                <Layout>
                  <CardCreatePage />
                </Layout>
              </ProtectedRoute>
            } />
            {/* PWA 단축키 호환성을 위한 리다이렉트 */}
            <Route path="/create" element={<Navigate to="/card/create" replace />} />
            <Route path="/firebase-test" element={
              <Layout>
                <FirebaseTestPage />
              </Layout>
            } />
            <Route path="/pwa-test" element={
              <Layout>
                <PWATestPage />
              </Layout>
            } />
            
            {/* Invoice management routes */}
            <Route path="/invoice/create" element={
              <Layout>
                <InvoiceCreationPage />
              </Layout>
            } />
            <Route path="/invoice/:cardId/:invoiceId/edit" element={
              <Layout>
                <InvoiceEditPage />
              </Layout>
            } />
            <Route path="/invoices" element={
              <Layout>
                <InvoiceListPage />
              </Layout>
            } />
            <Route path="/cards/:cardId/invoices" element={
              <Layout>
                <InvoiceListPage />
              </Layout>
            } />
            <Route path="/invoice-success" element={
              <Layout>
                <InvoiceSuccessPage />
              </Layout>
            } />
            <Route path="/business-profile" element={
              <Layout>
                <BusinessProfilePage />
              </Layout>
            } />
            
            {/* 통계 상세 페이지 */}
            <Route path="/stats/:cardId" element={
              <Layout>
                <StatsDetailPage />
              </Layout>
            } />
            <Route path="/stats/:cardId/:ownerId" element={
              <Layout>
                <StatsDetailPage />
              </Layout>
            } />
            
            {/* Card management routes - 동적 라우트보다 먼저 배치 */}
            <Route path="/cards/:cardId/qr-design" element={
              <Layout>
                <QRDesignPage />
              </Layout>
            } />
            <Route path="/cards/:cardId/edit" element={
              <Layout>
                <CardEditorPage />
              </Layout>
            } />
            <Route path="/domain-settings" element={
              <Layout>
                <DomainSettingsPage />
              </Layout>
            } />
            <Route path="/custom-domain-settings" element={
              <Layout>
                <CustomDomainSettingsPage />
              </Layout>
            } />
            
            {/* Subscription Management Page - 가장 우선순위로 배치 */}
            <Route path="/subscription-management" element={
              <Layout>
                <SubscriptionManagementPage />
              </Layout>
            } />
            
            {/* Subscription Management Page - 대체 경로들 */}
            <Route path="/subscription-management/" element={
              <Layout>
                <SubscriptionManagementPage />
              </Layout>
            } />
            <Route path="/subscription-management/*" element={
              <Layout>
                <SubscriptionManagementPage />
              </Layout>
            } />
            
            {/* Payment Success Page - username 동적 라우트보다 먼저 배치 */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            
            {/* Stripe Paywall Page - username 동적 라우트보다 먼저 배치 */}
            <Route path="/paywall" element={<StripePaywallPage />} />
            
            {/* Invoice edit route - username 동적 라우트보다 먼저 배치 */}
            <Route path="/invoices/:invoiceId/edit" element={
              <Layout>
                <InvoiceEditPage />
              </Layout>
            } />
            <Route path="/contact" element={
              <Layout>
                <ContactPage />
              </Layout>
            } />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/settings" element={
              <Layout>
                <SettingsPage />
              </Layout>
            } />

            {/* 🛡️ 관리자 전용 - 사용자 플랜 관리 (내부에서 관리자 여부 검증) */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <Layout>
                  <AdminPage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* username 동적 라우트: /u/t2design, /u/alice, /u/bob ... - 헤더/푸터 없음 */}
            {/* 주의: 이 라우트는 모든 경로를 잡아먹으므로 마지막에 배치해야 함 */}
            <Route path="/u/:username" element={<UserProfilePage />} />
            
            {/* 마지막에 catch-all 유지 (진짜 404만 루트로 보냄) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        
        {/* PWA 설치 프롬프트 - 모달 방식 (우선순위 높음) */}
        {showInstallPrompt && (
          <AddToHomeScreen
            onClose={handleInstallPromptClose}
            onInstall={handleInstallPromptInstall}
          />
        )}
        
        {/* PWA 설치 배너 - 모달이 표시되지 않을 때만 표시 */}
        {!showInstallPrompt && <PWAInstallBanner />}
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;